import angular from 'angular';
import {
  USAGE_DATA_UPDATED,
  PRODUCT_SIGNATURE_DELETED,
  PRODUCT_DISCONTINUED_EDITED,
  DISCONTINUED_PREFERENCE_CHANGED,
  PRODUCT_LABEL_COMMITTED,
  PRODUCT_DELETED,
  PRODUCT_DELETE_GUIDANCE,
  PRODUCT_DISCONTINUATION_GUIDANCE
} from 'common/messages';
import templateUrl from './products.html';

export default {
  templateUrl,
  bindings: {
    flex: '@',
    layout: '@',
    alerts: '<',
    administrationUnits: '<',
    animalTypes: '<',
    categories: '<',
    user: '<',
    products: '<',
    searchCriteria: '<',
    searchTypes: '<',
    query: '<',
    limitOptions: '<'
  },
  controller: class ProductsComponent {
    constructor($mdDialog, $mdMenu, $scope, $state,
      AccountService, AuthService, LoadingService,
      ProductService, ProductsService, ToastService,
      UsageDataService) {
      'ngInject';

      Object.assign(this, {
        $mdDialog,
        $mdMenu,
        $scope,
        $state,
        AccountService,
        AuthService,
        LoadingService,
        ProductService,
        ProductsService,
        ToastService,
        UsageDataService
      });

      this.onPaginationChanged = (page, take) => {
        this.query = {
          ...this.query,
          page,
          take
        };

        this.refreshResults();
      };

      this.onOrderChanged = (order) => {
        const direction = order.indexOf('-') !== -1 ? 'desc' : 'asc';
        const orderBy = order.replace('-', '');

        this.query = {
          ...this.query,
          direction,
          orderBy
        };

        this.refreshResults();
      };
    }

    $onInit() {
      this.searchCriteria = this.searchCriteria || {};
      this.selectedProducts = [];
    }

    refreshResults() {
      this.ProductsService.setQuery(this.query);
      this.promise = undefined;
      switch (this.searchCriteria.selectedSearchType) {
        case 'Search by Ingredient Name':
          if (this.searchCriteria.selectedUserIngredient) {
            this.promise = this.ProductsService
              .getProductsByIngredient(this.searchCriteria.selectedUserIngredient.UserIngredientId);
          }
          break;
        case 'Search by System Ingredient Name':
          if (this.searchCriteria.selectedSystemIngredient) {
            this.promise = this.ProductsService
              .getProductsBySystemIngredient(this.searchCriteria.selectedSystemIngredient.SystemIngredientId);
          }
          break;
        case 'Search by Member':
          if (this.searchCriteria.selectedCompany) {
            this.promise = this.ProductsService
              .getProductsByMember(this.searchCriteria.selectedCompany.MemberId);
          }
          break;
        case 'Search by Species':
          if (this.searchCriteria.selectedSpecies) {
            this.promise = this.ProductsService
              .getProductsBySpecies(this.searchCriteria.selectedSpecies);
          }
          break;
        case 'Missing - Containers Shipped Data':
          this.promise = this.ProductsService.getProductsMissingSalesData();
          break;
        case 'Missing - Product Information':
          this.promise = this.ProductsService.getProductsMissingProductInfo();
          break;
        default:
          this.promise = this.ProductsService.getProducts();
      }

      this.promise = this.promise || this.ProductsService.getProducts();

      this.promise.then((r) => {
        this.products = {
          Count: r.data.Count,
          Data: r.data.Data
        };
      }).catch(error => this.ToastService.showError(error));
    }

    onSearchChanged({ searchCriteria }) {
      this.searchCriteria = searchCriteria;
      this.query = {
        ...this.query,
        searchText: this.searchCriteria.searchText
      };

      this.refreshResults();
    }

    getSignatureImage(signature) {
      return signature && signature.length
        ? 'lock'
        : 'lock_open';
    }

    onEditClicked(productId) {
      this.$state.go('productEdit', { id: productId });
    }

    deleteSignature(product) {
      if (this.AuthService.isSysAdmin
        && product.Signature && product.Signature.length) {
        const confirm = this.$mdDialog.confirm()
          .title('Are you sure?')
          .textContent('Once confirmed, the product signature will be removed.')
          .ok('Remove Signature')
          .cancel('Cancel');

        this.$mdDialog.show(confirm).then(() => {
          this.ProductsService.removeSignature(product.ProductId)
            .then(() => {
              product.Signature = null;
              this.ToastService.showSuccess(PRODUCT_SIGNATURE_DELETED);
            }).catch(error => this.ToastService.showError(error));
        });
      }
    }

    setDiscontinued(product) {
      if (!product.Discontinued) {
        const confirm = this.$mdDialog.confirm()
          .title('Are you sure you want to discontinue this product?')
          .textContent(PRODUCT_DISCONTINUATION_GUIDANCE)
          .ok('Discontinue Product')
          .cancel('Cancel');

        this.$mdDialog.show(confirm).then(() => {
          // In this instance WITH the dialog, the value of product.Discontinued is correctly updated by the time we get here
          this.ProductsService.setDiscontinuedStatus(product.ProductId, product.Discontinued)
            .then(() => {
              this.ToastService.showSuccess(PRODUCT_DISCONTINUED_EDITED);
            }).catch(error => this.ToastService.showError(error));
        }).catch(() => {
          product.Discontinued = false;
          return false;
        });
      } else {
        // In this instance WITHOUT the dialog, the value of product.Discontinued hasn't had a chance to update yet so we have to pass its inverse
        this.ProductsService.setDiscontinuedStatus(product.ProductId, !product.Discontinued)
        .then(() => {
          this.ToastService.showSuccess(PRODUCT_DISCONTINUED_EDITED);
        }).catch(error => this.ToastService.showError(error));
      }
    }

    onViewClicked(productId, index) {
      this.LoadingService.start();
      this.ProductService.getProduct(productId).then((r) => {
        const product = r.data;

        const scope = angular.extend(this.$scope.$new(true), {
          product,
          units: this.products.Data[index].UnitPerContainer,
          categories: this.categories,
          administrationUnits: this.administrationUnits,
          administrations: product.tblDoses,
          showUsageGuidance: false,
          unitsDisplay: this.ProductService.getUnitsDisplay(product),
          locked: true,
          viewOnly: true
        });

        this.LoadingService.stop();

        this.$mdDialog.show({
          clickOutsideToClose: true,
          fullscreen: true,
          scope,
          template: product.IsTreat ? `
            <md-dialog class="flex-85" style="max-height: 90%">
              <md-dialog-content class="naers-dialog-content">
                <dialog-close/>
                <treat-information
                  administration-units="administrationUnits" 
                  categories="categories"
                  product="product"
                  units-display="unitsDisplay"
                  locked="locked"
                  view-only="viewOnly"
                  label-valid="true"
                  show-header="true">
                </treat-information>
                <treat-admin-list flex
                  units="units"
                  administrations="administrations"
                  units-display="unitsDisplay"
                  locked="locked"
                  show-header="true">
                </treat-admin-list>
                <product-ingredient-list flex ingredients="product.tblProduct_UserIngredient"
                  units-display="unitsDisplay"
                  locked="locked"
                  show-header="true">
                </product-ingredient-list>
                <usage-data flex usages="product.tblUsages" show-guidance="showUsageGuidance"
                  locked="locked"
                  show-header="true">
                </usage-data>
              </md-dialog-content>
            </md-dialog>
          ` : `
            <md-dialog class="flex-85" style="max-height: 90%">
              <md-dialog-content class="naers-dialog-content">
                <dialog-close/>
                <information
                  administration-units="administrationUnits" 
                  categories="categories"
                  product="product"
                  units-display="unitsDisplay"
                  locked="locked"
                  view-only="viewOnly"
                  label-valid="true"
                  show-header="true">
                </information>
                <loading-admin-list flex
                  ng-show="product.HasLoading"
                  administrations="administrations"
                  units-display="unitsDisplay"
                  locked="locked"
                  show-header="true">
                </loading-admin-list>
                <maintenance-admin-list flex
                  units="units"
                  administrations="administrations"
                  units-display="unitsDisplay"
                  locked="locked"
                  show-header="true">
                </maintenance-admin-list>
                <product-ingredient-list flex ingredients="product.tblProduct_UserIngredient"
                  units-display="unitsDisplay"
                  locked="locked"
                  show-header="true">
                </product-ingredient-list>
                <usage-data flex usages="product.tblUsages" show-guidance="showUsageGuidance"
                  locked="locked"
                  show-header="true">
                </usage-data>
              </md-dialog-content>
            </md-dialog>
          `
        });
      }).catch(error => this.ToastService.showError(error))
        .finally(() => this.LoadingService.stop());
    }

    showUsages(quarterOnly) {
      let promise;
      if (quarterOnly) {
        promise = this.UsageDataService.getMissingQuarterUsageData();
      } else {
        promise = this.UsageDataService.getMissingUsageData();
      }

      promise.then((r) => {
        const usages = r.data;

        if (usages && usages.length) {
          const scope = angular.extend(this.$scope.$new(true), {
            usages,
            onUsageDataUpdated: $event => this.onUsageDataUpdated($event)
          });

          this.$mdDialog.show({
            clickOutsideToClose: true,
            fullscreen: true,
            scope,
            template: `
          <md-dialog class="flex-85" style="max-height: 90%">
            <md-dialog-content>
              <dialog-close/>
              <bulk-usage-data
                usages="usages"
                on-usages-submitted="onUsageDataUpdated($event)">
              </bulk-usage-data>
            </md-dialog-content>
          </md-dialog>
          `
          });
        } else {
          this.$mdDialog.show(
            this.$mdDialog.alert()
              .clickOutsideToClose(true)
              .textContent('You are currently in compliance. No further action is required.')
              .ariaLabel('No action required')
              .ok('OK')
          );
        }
      }).catch(error => this.ToastService.showError(error));
    }

    onUsageDataUpdated($event) {
      if ($event && $event.usages) {
        this.UsageDataService.updateMissingUsageData($event.usages)
          .then(() => {
            this.ToastService.showSuccess(USAGE_DATA_UPDATED);
            this.$mdDialog.hide();
            this.$state.reload();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    onEditProductUsageClicked(product) {
      this.ProductService.getProduct(product.ProductId)
        .then((r) => {
          const scope = angular.extend(this.$scope.$new(true), {
            dialogTitle: `${product.ProductName} - Number of Units Shipped (Shipping Data)`,
            usages: this.ProductService.transformUsages(product.ProductId, product.DateIntroduced, r.data.tblUsages),
            usageDataChanged: $event => this.onProductUsageDataUpdated($event)
          });

          this.$mdDialog.show({
            clickOutsideToClose: true,
            fullscreen: true,
            scope,
            template: `
            <md-dialog class="flex-85" style="max-height: 90%">
              <md-dialog-content>
                <dialog-close/>
                <usage-data usages="usages"
                  show-guidance="true"
                  show-header="true"
                  dialog-title="{{dialogTitle}}"
                  show-action-buttons="true"
                  usage-data-changed="usageDataChanged($event)"
                  missing-message="Shipping data is currently in compliance for this product.">
                </usage-data>
              </md-dialog-content>
          </md-dialog>`
          });
        }).catch(error => this.ToastService.showError(error));
    }

    onProductUsageDataUpdated($event) {
      if ($event && $event.usages) {
        this.UsageDataService.updateMissingUsageData($event.usages)
          .then(() => {
            this.ToastService.showSuccess(USAGE_DATA_UPDATED);
            this.$mdDialog.hide();
          }).catch(error => this.ToastService.showError(error));
      }
    }

    alertClicked({ alert }) {
      if (alert) {
        if (alert.AlertTypeID === 1) {
          this.searchCriteria = {
            ...this.searchCriteria,
            selectedSearchType: 'Missing - Product Information'
          };

          this.refreshResults();
        } else if (alert.AlertTypeID === 3) {
          if (this.AuthService.isSysAdmin) {
            this.searchCriteria = {
              ...this.searchCriteria,
              selectedSearchType: 'Missing - Containers Shipped Data'
            };

            this.refreshResults();
          } else {
            this.showUsages(false);
          }
        }
      }
    }

    saveProductLabel({ productId, productLabel }) {
      this.LoadingService.start();
      this.ProductService.uploadLabel(productId, productLabel)
        .then(() => {
          this.ToastService.showSuccess(PRODUCT_LABEL_COMMITTED);
          this.$state.reload();
        }).catch(error => this.ToastService.showError(error))
        .finally(() => this.LoadingService.stop());
    }

    updateDiscontinuedSetting(showDiscontinuedProducts) {
      this.AccountService.updateDiscontinuedPreference(this.user.UserId, showDiscontinuedProducts)
        .then(() => {
          this.ToastService.showSuccess(DISCONTINUED_PREFERENCE_CHANGED);
          this.$state.reload();
        }).catch(error => this.ToastService.showError(error));
    }

    onDeleteClicked(products) {
      const confirm = this.$mdDialog.confirm()
        .title('Are you sure?')
        .textContent(PRODUCT_DELETE_GUIDANCE)
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        const promises = [];
        for (let i = products.length - 1; i >= 0; i -= 1) {
          promises.push(this.ProductService.delete(products[i].ProductId));
        }

        Promise.all(promises).then(() => {
          this.ToastService.showSuccess(PRODUCT_DELETED);
          this.refreshResults();
          this.selectedProducts = [];
        }).catch(error => this.ToastService.showError(error));
      });
    }

    onBulkUnsignClicked(products) {
      const confirm = this.$mdDialog.confirm()
        .title('Are you sure?')
        .textContent('Once confirmed, the product signature(s) will be removed.')
        .ok('Delete')
        .cancel('Cancel');

      this.$mdDialog.show(confirm).then(() => {
        const promises = [];
        for (let i = products.length - 1; i >= 0; i -= 1) {
          promises.push(this.ProductsService.removeSignature(products[i].ProductId));
        }

        Promise.all(promises).then(() => {
          this.ToastService.showSuccess(PRODUCT_SIGNATURE_DELETED);
          this.refreshResults();
          this.selectedProducts = [];
        }).catch(error => this.ToastService.showError(error));
      });
    }
  }
};



// WEBPACK FOOTER //
// ./components/app/products/products.component.js